import { supabase } from '../lib/supabase';
import { parseISO, differenceInDays, startOfMonth, endOfMonth, subMonths } from 'date-fns';

export const analyticsService = {
  // Get comprehensive analytics data
  async getAnalyticsData(dateRange = 'all') {
    try {
      const { data: bookings, error: bookingsError } = await supabase
        ?.from('consultation_bookings')
        ?.select('*')
        ?.order('scheduled_start', { ascending: false });

      if (bookingsError) throw bookingsError;

      const { data: leadScores, error: scoresError } = await supabase
        ?.from('lead_scores')
        ?.select('*');

      if (scoresError) throw scoresError;

      const { data: pipelines, error: pipelinesError } = await supabase
        ?.from('sales_pipeline')
        ?.select('*');

      if (pipelinesError) throw pipelinesError;

      return {
        bookings: bookings?.map(b => ({
          id: b?.id,
          clientId: b?.client_id,
          slotId: b?.slot_id,
          title: b?.title,
          description: b?.description,
          clientName: b?.client_name,
          clientEmail: b?.client_email,
          clientPhone: b?.client_phone,
          company: b?.company,
          serviceInterest: b?.service_interest,
          scheduledStart: b?.scheduled_start,
          scheduledEnd: b?.scheduled_end,
          durationMinutes: b?.duration_minutes,
          status: b?.status,
          meetingNotes: b?.meeting_notes,
          createdAt: b?.created_at,
          updatedAt: b?.updated_at
        })) || [],
        leadScores: leadScores?.map(s => ({
          id: s?.id,
          bookingId: s?.booking_id,
          engagementScore: s?.engagement_score,
          companySizeScore: s?.company_size_score,
          serviceFitScore: s?.service_fit_score,
          budgetScore: s?.budget_score,
          timelineScore: s?.timeline_score,
          totalScore: s?.total_score,
          scoreFactors: s?.score_factors,
          lastCalculatedAt: s?.last_calculated_at
        })) || [],
        pipelines: pipelines?.map(p => ({
          id: p?.id,
          bookingId: p?.booking_id,
          currentStage: p?.current_stage,
          stageHistory: p?.stage_history,
          estimatedValue: p?.estimated_value,
          probability: p?.probability,
          expectedCloseDate: p?.expected_close_date,
          notes: p?.notes,
          createdAt: p?.created_at,
          updatedAt: p?.updated_at
        })) || []
      };
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      throw error;
    }
  },

  // Calculate conversion metrics
  calculateConversionMetrics(bookings, pipelines) {
    const total = bookings?.length || 0;
    const confirmed = bookings?.filter(b => b?.status === 'confirmed')?.length || 0;
    const completed = bookings?.filter(b => b?.status === 'completed')?.length || 0;
    const cancelled = bookings?.filter(b => b?.status === 'cancelled')?.length || 0;
    const pending = bookings?.filter(b => b?.status === 'pending')?.length || 0;

    const dealsWon = pipelines?.filter(p => p?.currentStage === 'closed_won')?.length || 0;
    const dealsLost = pipelines?.filter(p => p?.currentStage === 'closed_lost')?.length || 0;
    const activeDeals = pipelines?.filter(p => 
      !['closed_won', 'closed_lost']?.includes(p?.currentStage)
    )?.length || 0;

    const conversionRate = completed > 0 ? ((dealsWon / completed) * 100)?.toFixed(1) : 0;
    const winRate = (dealsWon + dealsLost) > 0 ? ((dealsWon / (dealsWon + dealsLost)) * 100)?.toFixed(1) : 0;

    return {
      total,
      confirmed,
      completed,
      cancelled,
      pending,
      dealsWon,
      dealsLost,
      activeDeals,
      conversionRate,
      winRate
    };
  },

  // Calculate pipeline velocity (average days in each stage)
  calculatePipelineVelocity(pipelines) {
    const stageVelocity = {
      lead: [],
      qualified: [],
      proposal: [],
      negotiation: []
    };

    pipelines?.forEach(pipeline => {
      const history = pipeline?.stageHistory || [];
      for (let i = 0; i < history?.length - 1; i++) {
        const current = history?.[i];
        const next = history?.[i + 1];
        const days = differenceInDays(
          parseISO(next?.timestamp),
          parseISO(current?.timestamp)
        );
        if (stageVelocity?.[current?.stage]) {
          stageVelocity?.[current?.stage]?.push(days);
        }
      }
    });

    const averageVelocity = {};
    Object.keys(stageVelocity)?.forEach(stage => {
      const days = stageVelocity?.[stage];
      averageVelocity[stage] = days?.length > 0
        ? (days?.reduce((a, b) => a + b, 0) / days?.length)?.toFixed(1)
        : 0;
    });

    return averageVelocity;
  },

  // Calculate revenue forecasting by service type
  calculateRevenueForecasting(bookings, pipelines) {
    const serviceTypes = ['Data Strategy', 'Analytics Implementation', 'Governance Framework', 'Fractional CDO'];
    const forecasting = {};

    serviceTypes?.forEach(service => {
      const serviceBookings = bookings?.filter(b => b?.serviceInterest === service);
      const servicePipelines = pipelines?.filter(p => {
        const booking = bookings?.find(b => b?.id === p?.bookingId);
        return booking?.serviceInterest === service;
      });

      const totalValue = servicePipelines?.reduce((sum, p) => {
        const value = parseFloat(p?.estimatedValue || 0);
        const probability = parseFloat(p?.probability || 0) / 100;
        return sum + (value * probability);
      }, 0);

      const avgDealSize = servicePipelines?.length > 0
        ? servicePipelines?.reduce((sum, p) => sum + parseFloat(p?.estimatedValue || 0), 0) / servicePipelines?.length
        : 0;

      forecasting[service] = {
        consultations: serviceBookings?.length || 0,
        activeDeals: servicePipelines?.filter(p => !['closed_won', 'closed_lost']?.includes(p?.currentStage))?.length || 0,
        forecastedRevenue: totalValue?.toFixed(2),
        avgDealSize: avgDealSize?.toFixed(2)
      };
    });

    return forecasting;
  },

  // Get conversion funnel data
  getConversionFunnel(bookings, pipelines) {
    const totalLeads = bookings?.length || 0;
    const consultationsBooked = bookings?.filter(b => ['confirmed', 'completed']?.includes(b?.status))?.length || 0;
    const consultationsCompleted = bookings?.filter(b => b?.status === 'completed')?.length || 0;
    const proposalsSent = pipelines?.filter(p => ['proposal', 'negotiation', 'closed_won']?.includes(p?.currentStage))?.length || 0;
    const dealsClosed = pipelines?.filter(p => p?.currentStage === 'closed_won')?.length || 0;

    return [
      { stage: 'Leads', count: totalLeads, percentage: 100 },
      { stage: 'Consultations Booked', count: consultationsBooked, percentage: totalLeads > 0 ? ((consultationsBooked / totalLeads) * 100)?.toFixed(1) : 0 },
      { stage: 'Consultations Completed', count: consultationsCompleted, percentage: totalLeads > 0 ? ((consultationsCompleted / totalLeads) * 100)?.toFixed(1) : 0 },
      { stage: 'Proposals Sent', count: proposalsSent, percentage: totalLeads > 0 ? ((proposalsSent / totalLeads) * 100)?.toFixed(1) : 0 },
      { stage: 'Deals Closed', count: dealsClosed, percentage: totalLeads > 0 ? ((dealsClosed / totalLeads) * 100)?.toFixed(1) : 0 }
    ];
  },

  // Get time series data for trend analysis
  getTimeSeriesData(bookings, months = 6) {
    const now = new Date();
    const timeSeriesData = [];

    for (let i = months - 1; i >= 0; i--) {
      const monthDate = subMonths(now, i);
      const monthStart = startOfMonth(monthDate);
      const monthEnd = endOfMonth(monthDate);

      const monthBookings = bookings?.filter(b => {
        const bookingDate = parseISO(b?.scheduledStart);
        return bookingDate >= monthStart && bookingDate <= monthEnd;
      });

      timeSeriesData?.push({
        month: monthDate?.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
        consultations: monthBookings?.length || 0,
        completed: monthBookings?.filter(b => b?.status === 'completed')?.length || 0,
        cancelled: monthBookings?.filter(b => b?.status === 'cancelled')?.length || 0
      });
    }

    return timeSeriesData;
  },

  // ============ CONVERSION TRACKING METHODS ============

  /**
   * Track conversion event (assessment start, completion, booking start, confirmation)
   */
  async trackConversionEvent(eventData) {
    try {
      const { data: { user } } = (await supabase?.auth?.getUser()) || { data: { user: null } };
      
      const { data, error } = await supabase?.from('conversion_events')?.insert({
        user_id: user?.id || null,
        session_id: eventData?.sessionId,
        event_type: eventData?.eventType,
        event_category: eventData?.eventCategory,
        event_data: eventData?.eventData || {},
        page_path: eventData?.pagePath || window?.location?.pathname,
        referrer: eventData?.referrer || document?.referrer,
        user_agent: navigator?.userAgent
      })?.select()?.single();

      if (error) throw error;
      return data;
    } catch (error) {
      console.error('Error tracking conversion event:', error);
      throw error;
    }
  },

  /**
   * Create or update conversion funnel metric
   */
  async upsertConversionFunnel(funnelData) {
    try {
      const { data: { user } } = (await supabase?.auth?.getUser()) || { data: { user: null } };
      
      // Check if funnel record exists for this session
      const { data: existing } = await supabase
        ?.from('conversion_funnel_metrics')
        ?.select('*')
        ?.eq('session_id', funnelData?.sessionId)
        ?.single();

      if (existing) {
        // Update existing record
        const updates = {};
        if (funnelData?.bookingId) updates.booking_id = funnelData?.bookingId;
        if (funnelData?.serviceInterest) updates.service_interest = funnelData?.serviceInterest;
        if (funnelData?.conversionStatus) updates.conversion_status = funnelData?.conversionStatus;
        if (funnelData?.timeToBookingMinutes !== undefined) {
          updates.time_to_booking_minutes = funnelData?.timeToBookingMinutes;
        }
        
        // Recalculate lead quality score if we have all necessary data
        if (updates?.time_to_booking_minutes !== undefined || funnelData?.conversionStatus === 'booking_completed') {
          const { data: scoreData } = await supabase?.rpc('calculate_lead_quality_score', {
            p_assessment_score: existing?.assessment_score,
            p_maturity_level: existing?.maturity_level,
            p_service_interest: updates?.service_interest || existing?.service_interest,
            p_time_to_booking_minutes: updates?.time_to_booking_minutes || existing?.time_to_booking_minutes
          });
          if (scoreData !== null) updates.lead_quality_score = scoreData;
        }

        const { data, error } = await supabase
          ?.from('conversion_funnel_metrics')
          ?.update(updates)
          ?.eq('id', existing?.id)
          ?.select()
          ?.single();

        if (error) throw error;
        return data;
      } else {
        // Create new record
        const { data, error } = await supabase?.from('conversion_funnel_metrics')?.insert({
          user_id: user?.id || null,
          session_id: funnelData?.sessionId,
          assessment_id: funnelData?.assessmentId || null,
          booking_id: funnelData?.bookingId || null,
          assessment_type: funnelData?.assessmentType || null,
          assessment_score: funnelData?.assessmentScore || null,
          maturity_level: funnelData?.maturityLevel || null,
          service_interest: funnelData?.serviceInterest || null,
          time_to_booking_minutes: funnelData?.timeToBookingMinutes || null,
          conversion_status: funnelData?.conversionStatus || 'assessment_only',
          lead_quality_score: 0 // Will be calculated by trigger or subsequent update
        })?.select()?.single();

        if (error) throw error;
        return data;
      }
    } catch (error) {
      console.error('Error upserting conversion funnel:', error);
      throw error;
    }
  },

  /**
   * Get conversion funnel analytics
   */
  async getConversionFunnelAnalytics(dateRange = 'all') {
    try {
      let query = supabase?.from('conversion_funnel_metrics')?.select('*');

      // Apply date filter if needed
      if (dateRange !== 'all') {
        const now = new Date();
        let startDate;
        
        switch (dateRange) {
          case '7days':
            startDate = new Date(now?.getTime() - 7 * 24 * 60 * 60 * 1000);
            break;
          case '30days':
            startDate = new Date(now?.getTime() - 30 * 24 * 60 * 60 * 1000);
            break;
          case '90days':
            startDate = new Date(now?.getTime() - 90 * 24 * 60 * 60 * 1000);
            break;
          default:
            startDate = null;
        }

        if (startDate) {
          query = query?.gte('created_at', startDate?.toISOString());
        }
      }

      const { data, error } = await query?.order('created_at', { ascending: false });

      if (error) throw error;

      // Calculate aggregate metrics
      const totalAssessments = data?.length || 0;
      const bookingsStarted = data?.filter(d => ['booking_started', 'booking_completed']?.includes(d?.conversion_status))?.length || 0;
      const bookingsCompleted = data?.filter(d => d?.conversion_status === 'booking_completed')?.length || 0;
      const bookingsCancelled = data?.filter(d => d?.conversion_status === 'booking_cancelled')?.length || 0;

      const assessmentToBookingRate = totalAssessments > 0 
        ? ((bookingsCompleted / totalAssessments) * 100)?.toFixed(1) 
        : 0;

      const bookingCompletionRate = bookingsStarted > 0
        ? ((bookingsCompleted / bookingsStarted) * 100)?.toFixed(1)
        : 0;

      const avgLeadQualityScore = data?.length > 0
        ? (data?.reduce((sum, d) => sum + (d?.lead_quality_score || 0), 0) / data?.length)?.toFixed(1)
        : 0;

      const avgTimeToBooking = data?.filter(d => d?.time_to_booking_minutes)?.length > 0
        ? (data?.filter(d => d?.time_to_booking_minutes)?.reduce((sum, d) => sum + d?.time_to_booking_minutes, 0) / 
           data?.filter(d => d?.time_to_booking_minutes)?.length)?.toFixed(0)
        : 0;

      return {
        metrics: {
          totalAssessments,
          bookingsStarted,
          bookingsCompleted,
          bookingsCancelled,
          assessmentToBookingRate: parseFloat(assessmentToBookingRate),
          bookingCompletionRate: parseFloat(bookingCompletionRate),
          avgLeadQualityScore: parseFloat(avgLeadQualityScore),
          avgTimeToBookingMinutes: parseInt(avgTimeToBooking)
        },
        funnelData: data?.map(d => ({
          id: d?.id,
          userId: d?.user_id,
          sessionId: d?.session_id,
          assessmentId: d?.assessment_id,
          bookingId: d?.booking_id,
          assessmentType: d?.assessment_type,
          assessmentScore: d?.assessment_score,
          maturityLevel: d?.maturity_level,
          serviceInterest: d?.service_interest,
          timeToBookingMinutes: d?.time_to_booking_minutes,
          conversionStatus: d?.conversion_status,
          leadQualityScore: d?.lead_quality_score,
          createdAt: d?.created_at,
          updatedAt: d?.updated_at
        })) || []
      };
    } catch (error) {
      console.error('Error fetching conversion funnel analytics:', error);
      throw error;
    }
  },

  /**
   * Get conversion events for a session
   */
  async getSessionConversionEvents(sessionId) {
    try {
      const { data, error } = await supabase
        ?.from('conversion_events')
        ?.select('*')
        ?.eq('session_id', sessionId)
        ?.order('created_at', { ascending: true });

      if (error) throw error;

      return data?.map(e => ({
        id: e?.id,
        userId: e?.user_id,
        sessionId: e?.session_id,
        eventType: e?.event_type,
        eventCategory: e?.event_category,
        eventData: e?.event_data,
        pagePath: e?.page_path,
        referrer: e?.referrer,
        userAgent: e?.user_agent,
        createdAt: e?.created_at
      })) || [];
    } catch (error) {
      console.error('Error fetching session conversion events:', error);
      throw error;
    }
  },

  /**
   * Get lead quality distribution
   */
  async getLeadQualityDistribution(dateRange = 'all') {
    try {
      let query = supabase?.from('conversion_funnel_metrics')?.select('lead_quality_score, conversion_status');

      if (dateRange !== 'all') {
        const now = new Date();
        let startDate;
        
        switch (dateRange) {
          case '7days':
            startDate = new Date(now?.getTime() - 7 * 24 * 60 * 60 * 1000);
            break;
          case '30days':
            startDate = new Date(now?.getTime() - 30 * 24 * 60 * 60 * 1000);
            break;
          case '90days':
            startDate = new Date(now?.getTime() - 90 * 24 * 60 * 60 * 1000);
            break;
          default:
            startDate = null;
        }

        if (startDate) {
          query = query?.gte('created_at', startDate?.toISOString());
        }
      }

      const { data, error } = await query;

      if (error) throw error;

      // Categorize by quality score ranges
      const distribution = {
        high: { count: 0, converted: 0 },      // 75-100
        medium: { count: 0, converted: 0 },    // 50-74
        low: { count: 0, converted: 0 }        // 0-49
      };

      data?.forEach(record => {
        const score = record?.lead_quality_score || 0;
        const converted = record?.conversion_status === 'booking_completed';

        if (score >= 75) {
          distribution.high.count++;
          if (converted) distribution.high.converted++;
        } else if (score >= 50) {
          distribution.medium.count++;
          if (converted) distribution.medium.converted++;
        } else {
          distribution.low.count++;
          if (converted) distribution.low.converted++;
        }
      });

      // Calculate conversion rates for each quality tier
      Object.keys(distribution)?.forEach(tier => {
        distribution[tier].conversionRate = distribution?.[tier]?.count > 0
          ? ((distribution?.[tier]?.converted / distribution?.[tier]?.count) * 100)?.toFixed(1)
          : 0;
      });

      return distribution;
    } catch (error) {
      console.error('Error fetching lead quality distribution:', error);
      throw error;
    }
  }
};